import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, addDays, subDays, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ChevronLeft, ChevronRight, CheckCircle2, ChevronDown, Plus, Trash2,
  Moon, Baby as Diaper, Utensils, Sparkles, Shirt, MessageSquare, HeartPulse, Clock,
  Mic, Square, Wand2, Stethoscope, Star, X,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { enfantsApi, transmissionsApi } from '../../utils/api';

const HUMEURS = [
  { v: 'tres_bien', label: 'Très bien', emoji: '😄' },
  { v: 'bien', label: 'Bien', emoji: '🙂' },
  { v: 'moyen', label: 'Moyen', emoji: '😐' },
  { v: 'difficile', label: 'Difficile', emoji: '😕' },
];
const CHANGE_TYPES = [
  { v: 'seche', label: 'Couche sèche', pill: 'bg-emerald-50 text-emerald-700' },
  { v: 'urine', label: 'Urine', pill: 'bg-amber-50 text-amber-700' },
  { v: 'selle', label: 'Selle', pill: 'bg-orange-50 text-orange-700' },
  { v: 'selle_debordante', label: 'Selle débordante', pill: 'bg-red-50 text-red-700' },
];
const REPAS_QUALITE = [
  { v: 'bien', label: 'Bien mangé' },
  { v: 'moyen', label: 'Moyennement' },
  { v: 'peu', label: 'Peu mangé' },
  { v: 'refus', label: "N'a pas mangé" },
];
const ACTIVITES = [
  ['jeux_libres', 'Jeux libres'], ['motricite', 'Motricité'], ['peinture', 'Peinture / dessin'],
  ['lecture', 'Lecture / comptines'], ['musique', 'Musique / chant'], ['manipulation', 'Manipulation'],
  ['eau', "Jeux d'eau"], ['exterieur', 'Sortie / extérieur'], ['construction', 'Construction'],
  ['sensoriel', 'Éveil sensoriel'], ['cuisine', 'Atelier cuisine'], ['calme', 'Temps calme'],
];

const toMin = (t) => { if (!t || !t.includes(':')) return null; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
function dureeLabel(debut, fin) {
  const d = toMin(debut), f = toMin(fin);
  if (d == null || f == null || f <= d) return null;
  const m = f - d; return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}
function totalSommeil(sommeils) {
  let m = 0; (sommeils || []).forEach(s => { const d = toMin(s.debut), f = toMin(s.fin); if (d != null && f != null && f > d) m += f - d; });
  return m ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : null;
}
const parseArr = (v) => { if (Array.isArray(v)) return v; try { return JSON.parse(v) || []; } catch { return []; } };

function getInitials(p, n) { return `${(p || '').charAt(0)}${(n || '').charAt(0)}`.toUpperCase(); }
function AvatarCircle({ prenom, nom }) {
  const colors = ['bg-navy-100 text-navy-700', 'bg-gold-200 text-gold-700', 'bg-emerald-100 text-emerald-700', 'bg-rose-200 text-rose-700', 'bg-amber-200 text-amber-700', 'bg-cyan-100 text-cyan-700'];
  const idx = ((prenom || '').charCodeAt(0) || 0) % colors.length;
  return <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${colors[idx]}`}>{getInitials(prenom, nom)}</div>;
}

/* ---------- Section ---------- */
function Section({ icon: Icon, title, right, children }) {
  return (
    <div className="bg-white rounded-2xl border border-cream-300/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] uppercase tracking-wide text-gold-600 font-semibold flex items-center gap-1.5"><Icon size={13} /> {title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

/* ---------- Panneau de dictée vocale + structuration IA ---------- */
function DicteePanel({ prenom, onApply }) {
  const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [msg, setMsg] = useState(null);
  const recRef = useRef(null);
  const baseRef = useRef('');

  const start = () => {
    if (!SR) return;
    const rec = new SR();
    rec.lang = 'fr-FR'; rec.continuous = true; rec.interimResults = true;
    baseRef.current = transcript ? transcript.trim() + ' ' : '';
    rec.onresult = (e) => {
      let interim = '', final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript; else interim += r[0].transcript;
      }
      if (final) baseRef.current += final;
      setTranscript((baseRef.current + interim).trim());
    };
    rec.onerror = (e) => { setListening(false); if (e.error === 'not-allowed' || e.error === 'service-not-allowed') setMsg({ ok: false, text: "Accès au micro refusé. Autorisez le micro dans votre navigateur." }); };
    rec.onend = () => setListening(false);
    recRef.current = rec; setMsg(null);
    try { rec.start(); setListening(true); } catch { /* déjà démarré */ }
  };
  const stop = () => { try { recRef.current && recRef.current.stop(); } catch {} setListening(false); };
  useEffect(() => () => { try { recRef.current && recRef.current.stop(); } catch {} }, []);

  const analyser = async () => {
    if (!transcript.trim()) return;
    if (listening) stop();
    setAnalyzing(true); setMsg(null);
    try {
      const { data } = await transmissionsApi.dictee(transcript.trim());
      onApply(data);
      setMsg({ ok: true, text: 'Fiche pré-remplie ✓ Vérifiez et complétez ci-dessous, puis Enregistrez.' });
    } catch (e) {
      setMsg({ ok: false, text: (e.response && e.response.data && e.response.data.error) || "Erreur lors de l'analyse. Réessayez." });
    } finally { setAnalyzing(false); }
  };

  return (
    <div className="rounded-2xl border border-gold-300 bg-gradient-to-b from-gold-50 to-white p-4">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[12px] uppercase tracking-wide text-gold-700 font-semibold flex items-center gap-1.5"><Wand2 size={13} /> Dicter la journée{prenom ? ` de ${prenom}` : ''}</div>
        {transcript && <button type="button" onClick={() => { setTranscript(''); baseRef.current = ''; setMsg(null); }} className="text-[12px] text-navy-400 hover:text-red-500 flex items-center gap-1"><X size={13} /> Effacer</button>}
      </div>

      <div className="flex items-stretch gap-2">
        {SR && (
          <button type="button" onClick={listening ? stop : start}
            className={`flex-shrink-0 w-14 rounded-2xl flex flex-col items-center justify-center gap-0.5 text-white transition-colors ${listening ? 'bg-red-500 animate-pulse' : 'bg-navy-800 hover:bg-navy-900'}`}>
            {listening ? <Square size={18} /> : <Mic size={20} />}
            <span className="text-[9px] font-medium">{listening ? 'Stop' : 'Parler'}</span>
          </button>
        )}
        <textarea value={transcript} onChange={e => setTranscript(e.target.value)} rows={3}
          className="form-input rounded-xl flex-1 text-[14px]"
          placeholder={SR ? "Appuyez sur « Parler » et racontez la journée… (ou tapez ici)" : "Tapez la journée en texte libre, Georges la structurera…"} />
      </div>

      <div className="flex items-center gap-2 mt-2.5">
        <button type="button" onClick={analyser} disabled={!transcript.trim() || analyzing}
          className="apple-btn-primary flex items-center gap-1.5 disabled:opacity-50">
          <Wand2 size={15} /> {analyzing ? 'Analyse…' : 'Remplir avec Georges'}
        </button>
        {listening && <span className="text-[12px] text-red-500 font-medium flex items-center gap-1">● Écoute en cours…</span>}
      </div>
      {msg && <p className={`text-[12.5px] mt-2 ${msg.ok ? 'text-emerald-600' : 'text-red-500'}`}>{msg.text}</p>}
    </div>
  );
}

/* ---------- Formulaire de transmission ---------- */
function TransmissionForm({ enfant, transmission, structureId, date, onSaved }) {
  const init = () => ({
    heure_arrivee: transmission?.heure_arrivee || '', heure_depart: transmission?.heure_depart || '',
    humeur: transmission?.humeur || '',
    sommeils: transmission?.sommeils ? parseArr(transmission.sommeils) : [],
    changes: transmission?.changes ? parseArr(transmission.changes) : [],
    repas_midi_qualite: transmission?.repas_midi_qualite || '', repas_midi: transmission?.repas_midi || '',
    repas_gouter_qualite: transmission?.repas_gouter_qualite || '', repas_gouter: transmission?.repas_gouter || '',
    activites_cochees: transmission?.activites_cochees ? parseArr(transmission.activites_cochees) : [],
    activites: transmission?.activites || '',
    soins: transmission?.soins ? parseArr(transmission.soins) : [],
    evenements_notables: transmission?.evenements_notables || '',
    vetements_pretes: !!transmission?.vetements_pretes, vetements_details: transmission?.vetements_details || '',
    commentaire: transmission?.commentaire || '', sante: transmission?.sante || '',
    auteur: transmission?.auteur || '',
  });
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addSommeil = () => set('sommeils', [...form.sommeils, { debut: '', fin: '' }]);
  const updSommeil = (i, k, v) => set('sommeils', form.sommeils.map((s, j) => j === i ? { ...s, [k]: v } : s));
  const delSommeil = (i) => set('sommeils', form.sommeils.filter((_, j) => j !== i));
  const addChange = () => set('changes', [...form.changes, { heure: '', type: 'seche' }]);
  const updChange = (i, k, v) => set('changes', form.changes.map((c, j) => j === i ? { ...c, [k]: v } : c));
  const delChange = (i) => set('changes', form.changes.filter((_, j) => j !== i));
  const addSoin = () => set('soins', [...form.soins, { heure: '', type: '', detail: '' }]);
  const updSoin = (i, k, v) => set('soins', form.soins.map((s, j) => j === i ? { ...s, [k]: v } : s));
  const delSoin = (i) => set('soins', form.soins.filter((_, j) => j !== i));
  const toggleAct = (key) => set('activites_cochees', form.activites_cochees.includes(key) ? form.activites_cochees.filter(k => k !== key) : [...form.activites_cochees, key]);

  // Fusionne le résultat de la dictée IA dans le formulaire (l'équipe relit ensuite)
  const applyDictee = (r) => setForm(f => {
    const m = { ...f };
    ['heure_arrivee', 'heure_depart', 'humeur', 'repas_midi_qualite', 'repas_gouter_qualite'].forEach(k => { if (r[k]) m[k] = r[k]; });
    const appendText = (k) => { if (r[k]) m[k] = (f[k] && f[k].trim()) ? `${f[k].trim()}\n${r[k]}` : r[k]; };
    ['repas_midi', 'repas_gouter', 'activites', 'evenements_notables', 'sante', 'commentaire'].forEach(appendText);
    m.sommeils = [...f.sommeils, ...(r.sommeils || [])];
    m.changes = [...f.changes, ...((r.changes || []).map(c => ({ heure: c.heure || '', type: c.type })))];
    m.soins = [...f.soins, ...(r.soins || [])];
    m.activites_cochees = Array.from(new Set([...f.activites_cochees, ...(r.activites_cochees || [])]));
    return m;
  });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const payload = { ...form, enfant_id: enfant.id, structure_id: structureId, date };
      if (transmission?.id) await transmissionsApi.update(transmission.id, payload);
      else await transmissionsApi.create(payload);
      onSaved();
    } catch { setErr('Erreur lors de la sauvegarde'); } finally { setSaving(false); }
  };

  const QualBtns = ({ field }) => (
    <div className="flex flex-wrap gap-1.5">
      {REPAS_QUALITE.map(q => (
        <button type="button" key={q.v} onClick={() => set(field, form[field] === q.v ? '' : q.v)}
          className={`px-2.5 py-1 rounded-full text-[12px] border transition-colors ${form[field] === q.v ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-navy-500 border-cream-300'}`}>{q.label}</button>
      ))}
    </div>
  );

  return (
    <form onSubmit={submit} className="p-4 bg-cream-100/70 border-t border-cream-300/50 space-y-3">
      {/* Dictée vocale + structuration IA */}
      <DicteePanel prenom={enfant.prenom} onApply={applyDictee} />

      {/* Arrivée / départ / humeur */}
      <Section icon={Clock} title="Présence & humeur">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="text-[12px] text-navy-400">Arrivée</label><input type="time" value={form.heure_arrivee} onChange={e => set('heure_arrivee', e.target.value)} className="form-input rounded-xl mt-1" /></div>
          <div><label className="text-[12px] text-navy-400">Départ</label><input type="time" value={form.heure_depart} onChange={e => set('heure_depart', e.target.value)} className="form-input rounded-xl mt-1" /></div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {HUMEURS.map(h => (
            <button type="button" key={h.v} onClick={() => set('humeur', form.humeur === h.v ? '' : h.v)}
              className={`px-3 py-1.5 rounded-full text-[13px] border transition-colors ${form.humeur === h.v ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-navy-600 border-cream-300'}`}>{h.emoji} {h.label}</button>
          ))}
        </div>
      </Section>

      {/* Sommeil */}
      <Section icon={Moon} title="Sommeil" right={totalSommeil(form.sommeils) && <span className="text-[12px] font-semibold text-gold-700">Total {totalSommeil(form.sommeils)}</span>}>
        <div className="space-y-2">
          {form.sommeils.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="time" value={s.debut} onChange={e => updSommeil(i, 'debut', e.target.value)} className="form-input rounded-xl !w-auto flex-1" />
              <span className="text-navy-300">→</span>
              <input type="time" value={s.fin} onChange={e => updSommeil(i, 'fin', e.target.value)} className="form-input rounded-xl !w-auto flex-1" />
              <span className="text-[12px] text-navy-500 w-14 text-center">{dureeLabel(s.debut, s.fin) || '—'}</span>
              <button type="button" onClick={() => delSommeil(i)} className="p-1.5 text-navy-300 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
          <button type="button" onClick={addSommeil} className="text-[13px] text-navy-600 hover:text-navy-800 flex items-center gap-1"><Plus size={14} /> Ajouter une sieste</button>
        </div>
      </Section>

      {/* Changes */}
      <Section icon={Diaper} title="Change">
        <div className="space-y-2">
          {form.changes.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="time" value={c.heure} onChange={e => updChange(i, 'heure', e.target.value)} className="form-input rounded-xl !w-auto w-28" />
              <select value={c.type} onChange={e => updChange(i, 'type', e.target.value)} className="form-input rounded-xl flex-1">
                {CHANGE_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
              <button type="button" onClick={() => delChange(i)} className="p-1.5 text-navy-300 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
          <button type="button" onClick={addChange} className="text-[13px] text-navy-600 hover:text-navy-800 flex items-center gap-1"><Plus size={14} /> Ajouter un change</button>
        </div>
      </Section>

      {/* Soins / DRP / traitements */}
      <Section icon={Stethoscope} title="Soins & traitements">
        <div className="space-y-2">
          {form.soins.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="time" value={s.heure} onChange={e => updSoin(i, 'heure', e.target.value)} className="form-input rounded-xl !w-auto w-24 flex-shrink-0" />
              <input value={s.type} onChange={e => updSoin(i, 'type', e.target.value)} className="form-input rounded-xl w-28 flex-shrink-0" placeholder="Type (DRP…)" />
              <input value={s.detail} onChange={e => updSoin(i, 'detail', e.target.value)} className="form-input rounded-xl flex-1" placeholder="Détail (ex. Doliprane 2,5 ml)" />
              <button type="button" onClick={() => delSoin(i)} className="p-1.5 text-navy-300 hover:text-red-500 flex-shrink-0"><Trash2 size={15} /></button>
            </div>
          ))}
          <button type="button" onClick={addSoin} className="text-[13px] text-navy-600 hover:text-navy-800 flex items-center gap-1"><Plus size={14} /> Ajouter un soin</button>
        </div>
      </Section>

      {/* Repas */}
      <Section icon={Utensils} title="Repas">
        <div className="space-y-3">
          <div>
            <div className="text-[13px] font-medium text-navy-700 mb-1.5">Déjeuner</div>
            <QualBtns field="repas_midi_qualite" />
            <input value={form.repas_midi} onChange={e => set('repas_midi', e.target.value)} className="form-input rounded-xl mt-2" placeholder="Détail (optionnel) : ex. a tout fini, n'a pas aimé les légumes…" />
          </div>
          <div>
            <div className="text-[13px] font-medium text-navy-700 mb-1.5">Goûter</div>
            <QualBtns field="repas_gouter_qualite" />
            <input value={form.repas_gouter} onChange={e => set('repas_gouter', e.target.value)} className="form-input rounded-xl mt-2" placeholder="Détail (optionnel)" />
          </div>
        </div>
      </Section>

      {/* Activités */}
      <Section icon={Sparkles} title="Activités">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {ACTIVITES.map(([key, label]) => (
            <button type="button" key={key} onClick={() => toggleAct(key)}
              className={`px-2.5 py-1 rounded-full text-[12.5px] border transition-colors ${form.activites_cochees.includes(key) ? 'bg-gold-500 text-white border-gold-500' : 'bg-white text-navy-500 border-cream-300'}`}>{label}</button>
          ))}
        </div>
        <textarea value={form.activites} onChange={e => set('activites', e.target.value)} rows={2} className="form-input rounded-xl" placeholder="Précisions sur les activités…" />
      </Section>

      {/* Vêtements + commentaire + santé */}
      <Section icon={Shirt} title="Vêtements prêtés">
        <label className="flex items-center gap-2 text-[13.5px] text-navy-700 cursor-pointer">
          <input type="checkbox" checked={form.vetements_pretes} onChange={e => set('vetements_pretes', e.target.checked)} className="w-4 h-4 accent-navy-700" />
          La crèche a prêté des vêtements
        </label>
        {form.vetements_pretes && <input value={form.vetements_details} onChange={e => set('vetements_details', e.target.value)} className="form-input rounded-xl mt-2" placeholder="Lesquels ? (body, pantalon, chaussettes…) à rendre" />}
      </Section>

      <Section icon={Star} title="Événements notables">
        <textarea value={form.evenements_notables} onChange={e => set('evenements_notables', e.target.value)} rows={2} className="form-input rounded-xl" placeholder="Ex. a fait ses premiers pas, a mordu un camarade, a dit un nouveau mot…" />
      </Section>

      <Section icon={MessageSquare} title="Commentaire">
        <textarea value={form.commentaire} onChange={e => set('commentaire', e.target.value)} rows={3} className="form-input rounded-xl" placeholder="Mot pour les parents, anecdote de la journée…" />
      </Section>

      <Section icon={HeartPulse} title="Santé / médicaments">
        <textarea value={form.sante} onChange={e => set('sante', e.target.value)} rows={2} className="form-input rounded-xl" placeholder="Ex. température 38°C à 14h, Doliprane donné…" />
      </Section>

      <div className="flex items-center justify-between gap-3 pt-1">
        <input value={form.auteur} onChange={e => set('auteur', e.target.value)} className="form-input rounded-xl flex-1" placeholder="Rédigé par (prénom)" />
        <button type="submit" disabled={saving} className="apple-btn-primary">{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
      {err && <p className="text-red-500 text-xs">{err}</p>}
    </form>
  );
}

/* ---------- Carte enfant ---------- */
function EnfantCard({ enfant, transmission, structureId, date, onSaved }) {
  const [open, setOpen] = useState(false);
  const t = transmission;
  const humeur = t?.humeur ? HUMEURS.find(h => h.v === t.humeur) : null;
  const nbChanges = t ? parseArr(t.changes).length : 0;
  const dodo = t ? totalSommeil(parseArr(t.sommeils)) : null;

  return (
    <div className="apple-card overflow-hidden">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream-100/70 transition-colors text-left">
        <AvatarCircle prenom={enfant.prenom} nom={enfant.nom} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-navy-900 text-[15px]">{enfant.prenom} {enfant.nom}</div>
          {t ? (
            <div className="text-[12px] text-navy-400 mt-0.5 flex flex-wrap gap-x-2">
              {humeur && <span>{humeur.emoji} {humeur.label}</span>}
              {t.heure_arrivee && <span>· arrivée {t.heure_arrivee}</span>}
              {dodo && <span>· 💤 {dodo}</span>}
              {nbChanges > 0 && <span>· {nbChanges} change{nbChanges > 1 ? 's' : ''}</span>}
            </div>
          ) : <div className="text-[12px] text-navy-300 mt-0.5">À renseigner</div>}
        </div>
        {t && <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />}
        <ChevronDown size={17} className={`text-navy-300 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <TransmissionForm enfant={enfant} transmission={t} structureId={structureId} date={date} onSaved={() => { onSaved(); }} />}
    </div>
  );
}

/* ---------- Page ---------- */
export default function TransmissionsPage() {
  const { selectedStructure, structures } = useApp();
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [enfants, setEnfants] = useState([]);
  const [transmissions, setTransmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const structureId = selectedStructure;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { statut: 'actif', ...(structureId ? { structure_id: structureId } : {}) };
      const [enfRes, trRes] = await Promise.all([
        enfantsApi.getAll(params),
        transmissionsApi.getAll({ date, ...(structureId ? { structure_id: structureId } : {}) }),
      ]);
      setEnfants(enfRes.data || []);
      setTransmissions(trRes.data || []);
    } catch { setEnfants([]); setTransmissions([]); } finally { setLoading(false); }
  }, [date, structureId]);
  useEffect(() => { load(); }, [load]);

  const getT = (id) => transmissions.find(t => t.enfant_id === id);
  const renseignes = enfants.filter(e => getT(e.id)).length;
  const dateLabel = format(parseISO(date), 'EEEE d MMMM yyyy', { locale: fr });

  return (
    <div className="-m-6 p-6 min-h-full bg-cream-200">
      <div className="max-w-3xl mx-auto pb-16">
        <div className="mb-5">
          <h1 className="font-serif text-[32px] leading-tight text-navy-900">Transmissions</h1>
          <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
          <p className="text-navy-400 text-[14px]">La journée de chaque enfant — sommeil, changes, repas, activités.</p>
        </div>

        {/* Sélecteur de date */}
        <div className="flex items-center justify-between apple-card px-4 py-3 mb-4">
          <button onClick={() => setDate(d => format(subDays(parseISO(d), 1), 'yyyy-MM-dd'))} className="p-1.5 rounded-lg hover:bg-cream-100 text-navy-400"><ChevronLeft size={18} /></button>
          <div className="text-center relative">
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="sr-only" id="date-picker" />
            <label htmlFor="date-picker" className="cursor-pointer font-semibold text-navy-800 capitalize hover:text-gold-600">{dateLabel}</label>
          </div>
          <button onClick={() => setDate(d => format(addDays(parseISO(d), 1), 'yyyy-MM-dd'))} className="p-1.5 rounded-lg hover:bg-cream-100 text-navy-400"><ChevronRight size={18} /></button>
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-[13px] text-navy-500"><span className="font-semibold text-navy-800">{renseignes}/{enfants.length}</span> enfants renseignés</p>
          <span className="text-[11px] bg-navy-50 text-navy-500 px-2 py-1 rounded-full">{structureId ? structures.find(s => s.id === structureId)?.nom : 'Toutes les structures'}</span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-navy-300">Chargement…</div>
        ) : enfants.length === 0 ? (
          <div className="apple-card text-center py-14 text-navy-300">Aucun enfant actif.</div>
        ) : (
          <div className="space-y-3">
            {enfants.map(e => <EnfantCard key={e.id} enfant={e} transmission={getT(e.id)} structureId={structureId} date={date} onSaved={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}
